import type { QuickFillItem } from '../../../shared/types';
import type { QuickFillSnapshotShapeOption } from '../../../shared/api/grpc/familyClient';
import {
  buildElementParamMap,
  EMPTY_RESOLVED,
  isMeaningful,
  normalize,
  rowKey,
  stableTypeKey,
  type CheckedPropertyWithLookup,
  type PipeElement,
  type PipeFamily,
  type ProjectVisibleType,
  type ProjectVisibleTypeIndex,
  type RenderableFamilyType,
  type ResolvedParam,
} from './quickFillDataTypes';
import {
  applyResolvedValueToItem,
  resolveInstanceScopedParamValue,
} from './quickFillParameterResolution';

const getRenderableTypesForFamily = (
  family: PipeFamily,
  projectVisibleTypeIndex: ProjectVisibleTypeIndex,
  hasProjectElementRows: boolean
): RenderableFamilyType[] => {
  const familyTypes = Array.isArray(family.types) ? family.types : [];
  const familyTypesById = new Map(
    familyTypes
      .map((familyType) => [stableTypeKey(familyType.id), familyType] as const)
      .filter((entry): entry is [string, PipeFamily['types'][number]] => Boolean(entry[0]))
  );
  const familyTypesByName = new Map(familyTypes.map((familyType) => [normalize(familyType.name), familyType]));

  const renderableTypes: RenderableFamilyType[] = [];
  const renderedKeys = new Set<string>();
  const addRenderableType = (renderableType: RenderableFamilyType) => {
    const keys = [
      stableTypeKey(renderableType.typeId),
      stableTypeKey(renderableType.familyType?.id),
      `name:${normalize(renderableType.name)}`,
    ].filter((key): key is string => Boolean(key));
    if (keys.some((key) => renderedKeys.has(key))) {
      return;
    }

    keys.forEach((key) => renderedKeys.add(key));
    renderableTypes.push(renderableType);
  };

  if (hasProjectElementRows) {
    const projectVisibleTypes = projectVisibleTypeIndex.byFamilyName.get(normalize(family.name)) || [];
    for (const visibleType of projectVisibleTypes) {
      const visibleTypeIdKey = stableTypeKey(visibleType.typeId);
      const matchingType =
        (visibleTypeIdKey ? familyTypesById.get(visibleTypeIdKey) : undefined) ??
        familyTypesByName.get(normalize(visibleType.name)) ??
        null;
      addRenderableType({
        name: matchingType?.name ?? visibleType.name,
        familyName: visibleType.familyName,
        familyType: matchingType,
        typeId: visibleType.typeId ?? matchingType?.id,
      });
    }

    for (const familyType of familyTypes) {
      const visibleType = projectVisibleTypeIndex.byTypeId.get(stableTypeKey(familyType.id) || '');
      if (!visibleType) {
        continue;
      }

      addRenderableType({
        name: familyType.name,
        familyName: visibleType.familyName,
        familyType,
        typeId: visibleType.typeId ?? familyType.id,
      });
    }
  }

  for (const familyType of familyTypes) {
    addRenderableType({
      name: familyType.name,
      familyName: family.name,
      familyType,
      typeId: familyType.id,
    });
  }

  return renderableTypes;
};

const getRenderableTypeId = (renderableType: RenderableFamilyType): string | undefined =>
  renderableType.typeId || renderableType.familyType?.id;

const getItemFamilyTypeId = (item: QuickFillItem): string | undefined => {
  const value = (item as any).__familyTypeId;
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined;
};

const resolveModelElementIdsForFamilyType = (
  modelElementIdIndex: Map<string, string[]>,
  familyName: string,
  typeName: string | null,
  familyTypeId?: string | null
): string[] => {
  const ids = new Set<string>();
  const typeIndexKey = stableTypeKey(familyTypeId);

  if (typeIndexKey) {
    modelElementIdIndex.get(typeIndexKey)?.forEach((elementId) => ids.add(elementId));
  }
  modelElementIdIndex.get(rowKey(familyName, typeName))?.forEach((elementId) => ids.add(elementId));

  if (!typeName) {
    modelElementIdIndex.get(rowKey(familyName, null))?.forEach((elementId) => ids.add(elementId));
  }

  return Array.from(ids);
};

const applyModelElementIdsToItem = (
  item: QuickFillItem,
  familyName: string,
  typeName: string | null,
  familyTypeId: string | null | undefined,
  modelElementIdIndex: Map<string, string[]>
): boolean => {
  const elementIds = resolveModelElementIdsForFamilyType(
    modelElementIdIndex,
    familyName,
    typeName,
    familyTypeId
  );
  const previous =
    Array.isArray((item as any).__modelElementIds)
      ? ((item as any).__modelElementIds as unknown[]).map((id) => String(id))
      : [];
  const changed =
    previous.length !== elementIds.length ||
    previous.some((elementId, index) => elementId !== elementIds[index]);

  if (elementIds.length > 0) {
    (item as any).__modelElementIds = elementIds;
    return changed;
  }

  delete (item as any).__modelElementIds;
  return changed;
};

const buildFamilyTypeKey = (familyName: string, typeName: string | null): string =>
  typeName && typeName.trim() !== '' ? `${familyName}:${typeName}` : familyName;

const getRenderableFamilyName = (fallbackFamilyName: string, renderableType: RenderableFamilyType): string =>
  renderableType.familyName.trim() || fallbackFamilyName;

const buildRenderableFamilyTypeKey = (
  fallbackFamilyName: string,
  renderableType: RenderableFamilyType
): string => buildFamilyTypeKey(getRenderableFamilyName(fallbackFamilyName, renderableType), renderableType.name);

const buildInstanceRowLabel = (element: PipeElement): string => {
  const familyTypeLabel = buildFamilyTypeKey(element.familyName || 'Unknown Family', element.typeName ?? null);
  const elementName =
    typeof element.elementName === 'string' && element.elementName.trim() !== ''
      ? element.elementName.trim()
      : '';
  const preferredLabel =
    familyTypeLabel.trim() !== '' && familyTypeLabel !== 'Unknown Family'
      ? familyTypeLabel
      : elementName || familyTypeLabel;
  const elementIdSuffix =
    typeof element.elementId === 'string' && element.elementId.trim() !== ''
      ? ` (ID ${element.elementId.trim()})`
      : '';

  return `${preferredLabel}${elementIdSuffix}`;
};

const createInstanceQuickFillItem = (
  element: PipeElement,
  checkedProps: CheckedPropertyWithLookup[],
  typeParamMap: Map<string, ResolvedParam>,
  availableParameterNames: Set<string>,
  snapshotShape: QuickFillSnapshotShapeOption
): QuickFillItem => {
  const item: QuickFillItem = {
    Name: buildInstanceRowLabel(element),
  };
  const exactInstanceParamMap = buildElementParamMap(element);
  const familyTypeKey = buildFamilyTypeKey(element.familyName || 'Unknown Family', element.typeName ?? null);
  const instanceHasParamFlags: Record<string, boolean> = {};
  const typeHasParamFlags: Record<string, boolean> = {};

  checkedProps.forEach((prop) => {
    const normalizedParameterName = prop.normalizedRevitParameterName;
    const exactInstanceResolved = prop.revitParameter ? exactInstanceParamMap.get(prop.revitParameter) : undefined;
    const hasInstanceParam = exactInstanceResolved?.bindingScope === 'instance';
    const familyTypeResolved = prop.revitParameter ? typeParamMap.get(prop.revitParameter) : undefined;
    const hasTypeParam = familyTypeResolved?.bindingScope === 'type';
    const scopeHint = prop.bindingScopeHint;
    const resolved = prop.revitParameter
      ? exactInstanceResolved?.bindingScope === 'instance'
        ? exactInstanceResolved
        : scopeHint === 'instance'
        ? resolveInstanceScopedParamValue(
            exactInstanceParamMap,
            typeParamMap,
            prop.revitParameter,
            availableParameterNames,
            scopeHint
          )
        : scopeHint === 'type'
          ? typeParamMap.get(prop.revitParameter) ??
            (exactInstanceResolved?.bindingScope === 'type' ? exactInstanceResolved : undefined) ??
            (availableParameterNames.has(normalizedParameterName)
              ? {
                  value: '',
                  isReadOnly: false,
                  hasParameter: false,
                  bindingScope: 'type',
                }
              : EMPTY_RESOLVED)
          : exactInstanceParamMap.get(prop.revitParameter) ??
            typeParamMap.get(prop.revitParameter) ??
            (availableParameterNames.has(normalizedParameterName)
              ? {
                  value: '',
                  isReadOnly: false,
                  hasParameter: false,
                  bindingScope: 'unknown',
                }
              : EMPTY_RESOLVED)
      : EMPTY_RESOLVED;

    applyResolvedValueToItem(item, prop.label, resolved, true);
    instanceHasParamFlags[prop.label] = hasInstanceParam;
    typeHasParamFlags[prop.label] = hasTypeParam;
  });

  (item as any).__rowMode = 'instance';
  (item as any).__elementId = element.elementId || '';
  (item as any).__elementName = element.elementName || '';
  (item as any).__familyName = element.familyName || '';
  (item as any).__typeName = element.typeName || '';
  (item as any).__familyTypeKey = familyTypeKey;
  (item as any).__familyTypeId = element.typeId || '';
  (item as any).__category = element.category || '';
  (item as any).__instanceHasParamFlags = instanceHasParamFlags;
  (item as any).__typeHasParamFlags = typeHasParamFlags;
  (item as any).__quickFillSnapshotShape = snapshotShape;
  if (snapshotShape === 'instanceOnly') {
    (item as any).__forceInstanceParameterWrites = true;
  }

  return item;
};

export {
  isMeaningful,
  normalize,
  rowKey,
  stableTypeKey,
  buildElementParamMap,
  getRenderableTypesForFamily,
  getRenderableTypeId,
  getItemFamilyTypeId,
  resolveModelElementIdsForFamilyType,
  applyModelElementIdsToItem,
  buildFamilyTypeKey,
  getRenderableFamilyName,
  buildRenderableFamilyTypeKey,
  buildInstanceRowLabel,
  createInstanceQuickFillItem,
};

export type {
  PipeFamily,
  PipeElement,
  ProjectVisibleType,
  ProjectVisibleTypeIndex,
  RenderableFamilyType,
};
